import React, { useMemo } from 'react'
import { Box, Icon, H6 } from '@maui/elements'
import { SearchResultDescription, SearchResultHeader, SearchResultTitle, SearchResultPill, SearchResultPillMore } from '../common'
import IndeterminateIndicatorIcon from '../../icons/IndeterminateIndicatorIcon'
import { encodeURIComponentTwice } from '../../../utilities'
import { CopyButton } from '../searchPreviewComp/common'

const ICScorePill = ({ icScore }) => {
  const icon = useMemo(() => {
    if (icScore >= 0 && icScore <= 40) {
      return <Icon name='check-circle' color='colorRiskLow'/>
    } else if (icScore >= 41 && icScore <= 60) {
      return <IndeterminateIndicatorIcon/>
    } else if (icScore >= 61 && icScore <= 100) {
      return <Icon name='alert-triangle' color='colorRiskCritical' />
    }
  }, [icScore])
  const type = (
    <>
      {icon}
      <Box textStyle='bodySmall' m={0}>IC-Score :</Box>
    </>
  )
  const value = <Box textStyle='buttonLabelSmall' m={0}>{icScore}</Box>
  return (
    <SearchResultPill
      type={type}
      value={value}
    />
  )
}

export const ActorAssociationPill = ({ associatedActors = [] }) => {
  if (!associatedActors?.length) return null
  const { name } = associatedActors[0]
  const type = <Box textStyle='bodySmall' m={0}>Associated Actors :</Box>
  const value = (
    <Box display='flex' gap='4px'>
      <Icon name='user-secret' />
      <Box textStyle='buttonLabelSmall' m={0}>{name}</Box>
      <SearchResultPillMore values={associatedActors}/>
    </Box>
  )
  return (
    <SearchResultPill
      type={type}
      value={value}
    />
  )
}

const MalwareAssociationPill = ({ associatedMalware = [] }) => {
  if (!associatedMalware?.length) return null
  const { name } = associatedMalware[0]
  const type = <Box textStyle='bodySmall' m={0}>Associated Malware :</Box>
  const value = (
    <Box display='flex' gap='4px'>
      <Icon name='bug' />
      <Box textStyle='buttonLabelSmall' m={0}>{name}</Box>
      <SearchResultPillMore values={associatedMalware}/>
    </Box>
  )
  return (
    <SearchResultPill
      type={type}
      value={value}
    />
  )
}

export const IndicatorSearchResult = ({ data }) => {
  const {
    value: name,
    type,
    last_updated: date,
    description,
    mscore: icScore,
    attributed_associations: attributedAssociations = [],
    associated_hashes: associatedHashes = []
  } = data

  const associatedActors = attributedAssociations?.filter((assoc) => {
    return assoc.type === 'threat-actor'
  })
  const associatedMalware = attributedAssociations?.filter((assoc) => {
    return assoc.type === 'malware'
  })

  const link = useMemo(() => {
    if (!data) {
      return `/indicator/${type}${name ? `/${encodeURIComponentTwice(name)}` : ''}`
    }

    // encoding because the value can be a url here
    return `/indicator/${type}/${encodeURIComponentTwice(name)}`
  }, [data, name, type])
  return (
    <Box>
      <SearchResultHeader subHeaderText={`Indicator of Compromise - ${type}`} date={date} />
      <SearchResultTitle
        titleIcon={<Icon name='fingerprint' height='32px' width='32px'></Icon>}
        name={name}
        url={link}
      />
      <Box pt='12px' display='flex' gap='8px'>
        <ICScorePill icScore={icScore}/>
        <ActorAssociationPill associatedActors={associatedActors}/>
        <MalwareAssociationPill associatedMalware={associatedMalware}/>
      </Box>
      <Box mb={associatedHashes.length ? '16px' : '0'}>
        <SearchResultDescription description={description}/>
      </Box>
      {associatedHashes.map((associatedHash, index) => (
        <Box key={index} display='flex' mb='4px'>
          <H6 width='80px'm={0} color='colorTextDeemphasis'>{associatedHash.type?.toUpperCase()}</H6>
          <Box>
            <H6 overflow='hidden' whiteSpace='nowrap' color='colorTextMain' m='0' textOverflow='ellipsis'>{associatedHash.value}</H6>
          </Box>
          <Box marginLeft='8px'>
            <CopyButton value={associatedHash.value} />
          </Box>
        </Box>
      )).reverse()}
    </Box>
  )
}
